// src/components/InventoryUnitsModal.js
import React, { useEffect, useState, useContext, useMemo } from 'react';
import { 
    FiX, FiGrid, FiClock, FiActivity, FiArrowRight, FiShoppingCart, FiHash, FiCopy, FiTrash2, FiLayers 
} from 'react-icons/fi';
import { QRCodeCanvas } from 'qrcode.react'; 
import { useNavigate } from 'react-router-dom';
import Modal from './Modal';
import AssemblyModal from './AssemblyModal';
import { useApi } from '../hooks/useApi';
import { AuthContext } from '../context/AuthContext';
import { useModal } from '../context/ModalContext';
import { useIsMobile } from '../hooks/useIsMobile';

const UnitQRCodeModal = ({ isOpen, onClose, unit }) => {
    if (!isOpen || !unit) return null;
    const checkoutUrl = `${window.location.origin}/inventory/checkout-unit/${unit._id}`;

    return (
        <div className="modal-overlay printable-modal" onClick={onClose} style={{zIndex: 3100}}>
            <div className="modal-content modal-content-slim" onClick={e => e.stopPropagation()} style={{textAlign: 'center'}}>
                <div className="modal-header">
                    <h3>Serial: {unit.serialNumber || 'N/A'} </h3>
                    <button onClick={onClose} className="modal-close-btn"><FiX /></button>
                </div>
                <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem', padding: '2rem' }}>
                    <div style={{ padding: '1rem', background: 'white', borderRadius: '8px', border: '1px solid #ccc' }}>
                        <QRCodeCanvas value={checkoutUrl} size={200} level={"H"} />
                    </div>
                    <div>
                        <strong>{unit.name}</strong>
                        <p className="item-meta" style={{fontSize: '0.9rem', color: '#666', marginTop: '0.5rem'}}>
                            Scan to manage this specific unit
                        </p>
                    </div>
                </div>
                <div className="modal-footer">
                    <button className="btn btn-secondary" onClick={onClose}>Close</button>
                    <button className="btn btn-primary" onClick={() => window.print()}>Print</button>
                </div>
            </div>
        </div>
    );
};

const UnitHistoryModal = ({ isOpen, onClose, unit }) => {
    if (!isOpen || !unit) return null;
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleString('en-US', {
            month: 'short', day: 'numeric', year: '2-digit', hour: 'numeric', minute: '2-digit'
        });
    };

    return (
        <div className="modal-overlay" onClick={onClose} style={{zIndex: 3100}}>
            <div className="modal-content modal-content-wide" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <div style={{display:'flex', alignItems:'center', gap:'0.5rem'}}>
                        <FiActivity />
                        <h3 className="modal-title">Location Trail: {unit.serialNumber || unit.name}</h3>
                    </div>
                    <button onClick={onClose} className="modal-close-btn"><FiX /></button>
                </div>
                <div className="modal-body">
                    <div className="table-wrapper mobile-scroll-table">
                        <table style={{ width: '100%', fontSize:'0.9rem' }}>
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>Action</th>
                                    <th>Details / Location Path</th>
                                    <th>User</th>
                                </tr>
                            </thead>
                            <tbody>
                                {unit.history && unit.history.length > 0 ? (
                                    [...unit.history].reverse().map((log, index) => (
                                        <tr key={index}>
                                            <td style={{whiteSpace:'nowrap', color:'var(--gray-600)'}}>{formatDate(log.timestamp)} </td>
                                            <td style={{fontWeight:'600'}}>{log.action} </td>
                                            <td>
                                                {log.action === 'Transfer' && log.newOwnerName ? (
                                                    <span style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px', fontWeight: '500', color: 'var(--blue-800)' }}>
                                                        {log.previousOwnerName || 'Unknown'} 
                                                        <FiArrowRight size={12} style={{ color: 'var(--gray-400)' }}/> 
                                                        {log.newOwnerName}
                                                    </span>
                                                ) : log.action === 'Ticket Usage' && log.newOwnerName ? (
                                                    <span>Used on <strong>{log.newOwnerName}</strong></span>
                                                ) : log.action === 'Created' && log.newOwnerName ? (
                                                    <span>Added to <strong>{log.newOwnerName}</strong></span>
                                                ) : log.action === 'Return' && log.previousOwnerName ? (
                                                    <span>Returned from <strong>{log.previousOwnerName}</strong></span>
                                                ) : null}
                                                
                                                {log.notes && (
                                                    <div style={{ fontSize: '0.85rem', color: 'var(--gray-500)', fontStyle: 'italic', marginTop: '0.25rem' }}>
                                                        {log.notes}
                                                    </div>
                                                )}
                                            </td>
                                            <td>{log.performedBy?.name || 'System'}</td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr><td colSpan="4" className="empty-table-row">No history found.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
                <div className="modal-footer">
                    <button className="btn btn-secondary" onClick={onClose}>Close</button>
                </div>
            </div>
        </div>
    );
};

const InventoryUnitsModal = ({ isOpen, onClose, item, masterInventoryItems }) => {
    const { user } = useContext(AuthContext); 
    const { showConfirm, showAlert } = useModal(); 
    const isMobile = useIsMobile();
    const [units, setUnits] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedUnitIds, setSelectedUnitIds] = useState([]);
    
    const [qrUnit, setQrUnit] = useState(null); 
    const [historyUnit, setHistoryUnit] = useState(null); 
    const [assemblyUnit, setAssemblyUnit] = useState(null); 
    
    const [cursorTooltip, setCursorTooltip] = useState({ isVisible: false, x: 0, y: 0 });

    const callApi = useApi();
    const navigate = useNavigate();

    const isAdmin = useMemo(() => 
        user?.roles?.some(role => ['Giga Admin', 'Admin'].includes(role)), 
    [user]); 

    const isGigaAdmin = useMemo(() => user?.roles?.includes('Giga Admin'), [user]);

    useEffect(() => {
        if (isOpen && item) {
            setLoading(true);
            setSelectedUnitIds([]);
            callApi(`inventory/${item._id}/units`) 
                .then(res => {
                    if (res.success) {
                        setUnits(res.data);
                    }
                    setLoading(false);
                });
        }
    }, [isOpen, item, callApi]);

    const handleSelectUnit = (id) => {
        if (!isAdmin) return;
        setSelectedUnitIds(prev => 
            prev.includes(id) ? prev.filter(uid => uid !== id) : [...prev, id]
        );
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedUnitIds(units.map(u => u._id));
        } else {
            setSelectedUnitIds([]);
        }
    };

    const handleBulkCheckout = () => {
        if (selectedUnitIds.length === 0) return;
        navigate(`/inventory/checkout-unit/bulk?ids=${selectedUnitIds.join(',')}`);
    };

    const handleBulkDeleteUnits = () => {
        if (selectedUnitIds.length === 0) return;
        showConfirm(
            `Are you sure you want to permanently delete ${selectedUnitIds.length} units? This will reduce the stock counts accordingly.`,
            async () => {
                const res = await callApi('inventory/unit/delete-multiple', 'POST', { ids: selectedUnitIds });
                if (res.success) {
                    setUnits(prev => prev.filter(u => !selectedUnitIds.includes(u._id)));
                    setSelectedUnitIds([]);
                } else {
                    showAlert(res.error, "Delete Failed");
                }
            },
            'Bulk Delete Units',
            true
        );
    };
    
    const handleCopyLink = (e, unitId) => {
        e.stopPropagation();
        const x = e.clientX || (e.touches && e.touches[0] ? e.touches[0].clientX : window.innerWidth / 2);
        const y = e.clientY || (e.touches && e.touches[0] ? e.touches[0].clientY : window.innerHeight / 2);

        const link = `${window.location.origin}/inventory/checkout-unit/${unitId}`;
        navigator.clipboard.writeText(link)
            .then(() => {
                setCursorTooltip({ isVisible: true, x, y });
                setTimeout(() => setCursorTooltip({ isVisible: false, x: 0, y: 0 }), 1500);
            })
            .catch(err => console.error("Failed to copy link: ", err));
    };

    const handleDeleteUnit = (unitId, sn) => {
        showConfirm(
            `Are you sure you want to permanently delete unit ${sn || unitId}? This will reduce the stock count by 1.`,
            async () => {
                const res = await callApi(`inventory/unit/${unitId}`, 'DELETE');
                if (res.success) {
                    setUnits(prev => prev.filter(u => u._id !== unitId));
                } else {
                    showAlert(res.error, "Delete Failed");
                }
            },
            'Delete Unit',
            true
        );
    };

    if (!isOpen || !item) return null;

    const isAssemblyType = item?.itemType === 'Assembly' || item?.isAssembly;

    const footerContent = (
        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
            <div style={{ fontSize: '0.9rem', color: 'var(--gray-500)' }}>
                {isAdmin && selectedUnitIds.length > 0 && `${selectedUnitIds.length} units selected`}
            </div>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
                {item.buyAgainLink && (
                    <a 
                        href={item.buyAgainLink} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="btn btn-secondary"
                        style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', textDecoration: 'none' }}
                        title="Open product link"
                    >
                        <FiShoppingCart /> Buy Again
                    </a>
                )}

                <button className="btn btn-secondary" onClick={onClose}>Close</button>
                
                {isGigaAdmin && selectedUnitIds.length > 0 && (
                    <button className="btn btn-danger" onClick={handleBulkDeleteUnits}>
                        <FiTrash2 className="btn-icon" /> Delete Selected
                    </button>
                )}

                {isAdmin && selectedUnitIds.length > 0 && (
                    <button className="btn btn-primary" onClick={handleBulkCheckout}>
                        <FiArrowRight className="btn-icon" /> Checkout Selected
                    </button>
                )}
            </div>
        </div>
    );

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'available': return 'status-active';
            case 'installed': return 'status-closed'; 
            default: return 'status-pending'; 
        }
    };

    const MobileUnitCard = ({ unit }) => {
        const isSelected = selectedUnitIds.includes(unit._id);
        return (
            <div 
                className={`mobile-generic-card ${isSelected ? 'selected' : ''}`} 
                onClick={() => isAdmin && handleSelectUnit(unit._id)}
            >
                <div className="mobile-card-header">
                    <div className="mobile-card-main-content">
                        <div className="mobile-card-icon-container">
                            <FiHash />
                        </div>
                        <div className="mobile-card-text">
                            <h4 className="mobile-card-title">
                                {unit.serialNumber || (isAssemblyType ? `ASM-${unit._id.substring(18)}` : 'No Serial')}
                            </h4>
                            <p className="mobile-card-subtitle" style={{marginTop: '0.25rem', display: 'flex', alignItems: 'center'}}>
                                Status: 
                                <span className={`status-badge ${getStatusColor(unit.status)}`} style={{marginLeft: '6px', fontSize: '0.7rem'}}>
                                    {unit.status}
                                </span>
                            </p>
                        </div>
                    </div>
                    {isAdmin && (
                        <div style={{paddingLeft: '10px'}}>
                            <input 
                                type="checkbox" 
                                className="custom-checkbox" 
                                checked={isSelected} 
                                onChange={() => handleSelectUnit(unit._id)}
                                onClick={(e) => e.stopPropagation()}
                            />
                        </div>
                    )}
                </div>
                <div className="mobile-card-actions">
                    {/* ✅ MOBILE: Open Assembly Contents */}
                    {isAssemblyType && (
                        <button className="btn btn-secondary btn-sm" title="View Assembly Contents" onClick={(e) => { e.stopPropagation(); setAssemblyUnit(unit); }}>
                            <FiLayers /> Contents
                        </button>
                    )}
                    <button className="btn btn-secondary btn-sm" title="Location Trail" onClick={(e) => { e.stopPropagation(); setHistoryUnit(unit); }}>
                        <FiClock /> History
                    </button>
                    <button className="btn btn-secondary btn-sm" title="View QR" onClick={(e) => { e.stopPropagation(); setQrUnit(unit); }} disabled={!unit.serialNumber}>
                        <FiGrid /> QR
                    </button>
                    <button className="btn btn-secondary btn-sm" title="Copy Checkout Link" onClick={(e) => handleCopyLink(e, unit._id)}>
                        <FiCopy /> Link
                    </button>
                    <button className="btn btn-primary btn-sm" title="Checkout" onClick={(e) => { e.stopPropagation(); navigate(`/inventory/checkout-unit/${unit._id}`); }}>
                        <FiArrowRight /> Checkout
                    </button>
                    {isGigaAdmin && (
                        <button className="btn btn-danger btn-sm" title="Delete Unit" onClick={(e) => { e.stopPropagation(); handleDeleteUnit(unit._id, unit.serialNumber); }}>
                            <FiTrash2 /> Delete
                        </button>
                    )}
                </div>
            </div>
        );
    };

    return (
        <>
            <Modal
                isOpen={isOpen}
                onClose={onClose}
                title={`Manage Units: ${item.name}`}
                footer={footerContent}
                size="wide"
                className="inventory-units-modal"
            >
                <div className="mobile-units-stats-row">
                    <div className="stat-box">
                        <span className="stat-label">Total Stock </span>
                        <span className="stat-value">{item.quantity} </span>
                    </div>
                </div>

                <p style={{ color: 'var(--gray-500)', fontSize: '0.85rem', marginBottom: '1rem', fontStyle: 'italic' }}>
                    Click the History button on any unit to view its complete location trail.
                    {isAssemblyType && " Click the Layers icon to view and modify assembly contents."}
                </p>

                {loading ? (
                    <div className="loading-indicator">Loading units... </div>
                ) : (
                    <>
                        {isMobile ? (
                            <div className="mobile-inventory-list">
                                {isAdmin && units.length > 0 && (
                                    <div style={{ padding: '0.5rem 0.25rem', display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                                        <input 
                                            type="checkbox" 
                                            id="mobile-select-all"
                                            className="custom-checkbox" 
                                            checked={selectedUnitIds.length === units.length && units.length > 0}
                                            onChange={handleSelectAll}
                                        />
                                        <label htmlFor="mobile-select-all" style={{ fontWeight: '600', fontSize: '0.9rem' }}>
                                            Select All ({units.length} Units)
                                        </label>
                                    </div>
                                )}

                                {units.map((unit) => (
                                    <MobileUnitCard key={unit._id} unit={unit} />
                                ))}
                                {units.length === 0 && <div className="no-tasks-message">No units found.</div>}
                            </div>
                        ) : (
                            <div className="table-wrapper mobile-scroll-container">
                                <table className="units-mobile-table">
                                    <thead>
                                        <tr>
                                            {isAdmin && (
                                                <th style={{ width: '40px' }}>
                                                    <input 
                                                        type="checkbox" 
                                                        className="custom-checkbox" 
                                                        checked={selectedUnitIds.length === units.length && units.length > 0}
                                                        onChange={handleSelectAll}
                                                        title="Select All Units"
                                                    />
                                                </th>
                                            )}
                                            <th>SN </th>
                                            <th>Status </th>
                                            <th style={{textAlign: 'right'}}>Actions </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {units.map((unit) => (
                                            <tr 
                                                key={unit._id}
                                                className={selectedUnitIds.includes(unit._id) ? 'selected' : ''}
                                                onClick={() => isAdmin && handleSelectUnit(unit._id)}
                                                style={{ cursor: isAdmin ? 'pointer' : 'default' }}
                                            >
                                                {isAdmin && (
                                                    <td onClick={e => e.stopPropagation()}>
                                                        <input 
                                                            type="checkbox" 
                                                            className="custom-checkbox" 
                                                            checked={selectedUnitIds.includes(unit._id)}
                                                            onChange={() => handleSelectUnit(unit._id)}
                                                        />
                                                    </td>
                                                )}
                                                <td className="sn-cell">
                                                    {unit.serialNumber || (isAssemblyType ? `ASM-${unit._id.substring(18)}` : 'N/A')} 
                                                </td>
                                                <td>
                                                    <span className={`status-badge ${getStatusColor(unit.status)}`}>
                                                        {unit.status} 
                                                    </span>
                                                </td>
                                                <td className="actions-cell" onClick={e => e.stopPropagation()}>
                                                    <button 
                                                        className="btn btn-primary btn-sm" 
                                                        style={{ marginRight: '0.5rem' }}
                                                        onClick={() => navigate(`/inventory/checkout-unit/${unit._id}`)}
                                                    >
                                                        Checkout
                                                    </button>
                                                    {/* ✅ DESKTOP: Open Assembly Contents */}
                                                    {isAssemblyType && (
                                                        <button className="btn-icon-only" title="View Assembly Contents" onClick={() => setAssemblyUnit(unit)}>
                                                            <FiLayers /> 
                                                        </button>
                                                    )}
                                                    <button className="btn-icon-only" title="Copy Checkout Link" onClick={(e) => handleCopyLink(e, unit._id)}>
                                                        <FiCopy /> 
                                                    </button>
                                                    <button className="btn-icon-only" title="View Location Trail" onClick={() => setHistoryUnit(unit)}>
                                                        <FiClock /> 
                                                    </button>
                                                    <button className="btn-icon-only" title="View QR Code" onClick={() => setQrUnit(unit)} disabled={!unit.serialNumber}>
                                                        <FiGrid />
                                                    </button>
                                                    {isGigaAdmin && (
                                                        <button className="btn-icon-only btn-icon-danger" title="Delete Unit" onClick={(e) => { e.stopPropagation(); handleDeleteUnit(unit._id, unit.serialNumber); }} style={{ marginLeft: '4px' }}>
                                                            <FiTrash2 />
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </>
                )}
            </Modal>
            
            {/* ✅ MOVED TO BOTTOM: Ensures these modals render ON TOP of the parent InventoryUnitsModal in the DOM */}
            <AssemblyModal 
                isOpen={!!assemblyUnit} 
                onClose={() => setAssemblyUnit(null)} 
                parentUnit={assemblyUnit} 
                masterInventoryItems={masterInventoryItems} 
                onViewChildHistory={(child) => setHistoryUnit(child)}
            />

            <UnitQRCodeModal isOpen={!!qrUnit} onClose={() => setQrUnit(null)} unit={qrUnit} />
            
            {/* Renders last to ensure it pops over the AssemblyModal */}
            <UnitHistoryModal isOpen={!!historyUnit} onClose={() => setHistoryUnit(null)} unit={historyUnit} />
            
            {/* Floating Cursor Tooltip */}
            {cursorTooltip.isVisible && (
                <div style={{
                    position: 'fixed',
                    left: cursorTooltip.x,
                    top: cursorTooltip.y - 35, 
                    transform: 'translateX(-50%)',
                    backgroundColor: 'var(--gray-800)',
                    color: 'var(--white)',
                    padding: '6px 12px',
                    borderRadius: '6px',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    zIndex: 9999, 
                    pointerEvents: 'none',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                    animation: 'slideFadeInDown 0.2s ease-out'
                }}>
                    Link Copied!
                </div>
            )}
        </>
    );
};

export default InventoryUnitsModal;